import { Dialog, LinearProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../config.json'
import { DropAlert } from '../drops/DropAlert'
import { DropPaper } from '../drops/DropPaper'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { ImageUploadButton } from '../imageupload/ImageUploadButton'
import { useSharedState } from '../waxplorer/Waxplorer'
import InputField from '../common/util/InputField'
import MainButton from '../common/util/input/MainButton'

export const UpdateCollectionBannerDialog = ({
    open,
    setOpen,
    atomicCollectionData,
    updateAtomicCollectionData = () => {},
}) => {
    const { t } = useTranslation('common')
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [imageLoaded, setImageLoaded] = useState(false)
    const [banner, setBanner] = useState(null)
    const [imageUploadError, setImageUploadError] = useState(null)
    const [uploadProgress, setUploadProgress] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState(null)
    const [transactionId, setTransactionId] = useState(null)

    const saveCollection = async () => {
        setIsSaving(true)
        setError(null)
        setTransactionId(null)

        const collectionData = atomicCollectionData.data.data

        const actions = [getBoostAction(activeUser)]

        const data = []

        data.push({
            key: 'description',
            value: [
                'string',
                collectionData.description ? collectionData.description : '',
            ],
        })

        data.push({
            key: 'name',
            value: ['string', collectionData.name],
        })

        data.push({
            key: 'img',
            value: ['string', collectionData.img],
        })

        data.push({
            key: 'url',
            value: ['string', collectionData.url],
        })

        data.push({
            key: 'socials',
            value: ['string', collectionData.socials],
        })

        data.push({
            key: 'creator_info',
            value: ['string', collectionData.creator_info],
        })

        let temp
        if (collectionData.images) {
            temp = JSON.parse(collectionData.images)
            temp.banner_1920x500 = banner.url
        } else {
            temp = { banner_1920x500: banner.url, logo_512x512: '' }
        }

        data.push({
            key: 'images',
            value: ['string', JSON.stringify(temp)],
        })

        actions.push({
            account: 'atomicassets',
            name: 'setcoldata',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                collection_name: atomicCollectionData.data.collection_name,
                data: data,
            },
        })

        let saveError = null
        let saveTransactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            saveTransactionId = result?.response?.transaction_id
        } catch (e) {
            saveError = e.message
            setError(saveError)
            setIsSaving(false)
        }
        await sleep(3000)
        updateAtomicCollectionData()
        setTransactionId(saveTransactionId)
        setIsSaving(false)
    }

    useEffect(() => {
        if (imageLoaded) {
            const img = document.getElementById('banner_img')

            const width = img.naturalWidth
            const height = img.naturalHeight

            setBanner({
                url: banner.url,
                width: width,
                height: height,
                wrongSize: width !== 1920 || height !== 500,
                broken: false,
            })
            setImageLoaded(false)
        }
    }, [imageLoaded])

    const useOnChainImage = () => {
        if (atomicCollectionData?.data?.data?.images) {
            const parsedImages = JSON.parse(
                atomicCollectionData.data.data.images,
            )
            const url = parsedImages.banner_1920x500
                .trim()
                .replaceAll(config.ipfs, '')
            if (parsedImages.banner_1920x500) {
                setBanner({
                    url: url,
                    width: null,
                    height: null,
                    wrongSize: null,
                    broken: false,
                })
            }
        } else {
            setBanner({
                url: null,
                width: null,
                height: null,
                wrongSize: null,
                broken: false,
            })
        }
    }

    useEffect(() => {
        useOnChainImage()
    }, [atomicCollectionData])

    const close = () => {
        if (!isSaving) {
            setOpen(false)
            useOnChainImage()
            setError(null)
            setTransactionId(null)
        }
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="md" fullWidth>
            <DropPaper title={t('collection.update_banner')} noMargin>
                {atomicCollectionData === null ? (
                    <div>
                        <div className="font-lg italic">
                            Loading existing collection data...
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : isSaving ? (
                    <div>
                        <div className="font-lg italic">
                            Setting your new banner image...
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="text-center italic mb-2">Preview</div>
                        <div className="flex justify-center mb-5">
                            <div className="w-[650px] h-[167px] bg-black relative ">
                                {isUploading ? (
                                    <div className="relative top-1/2 -translate-y-1/2">
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                        />
                                    </div>
                                ) : banner && banner.broken === false ? (
                                    <img
                                        id="banner_img"
                                        src={config.ipfs + banner.url}
                                        onError={() => {
                                            const temp = structuredClone(banner)
                                            temp.broken = true
                                            setBanner(temp)
                                        }}
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                ) : banner &&
                                  banner.url?.length > 0 &&
                                  banner.broken === true ? (
                                    <div className="relative top-1/2 -translate-y-1/2 text-center font-bold italic">
                                        - Invalid Image -
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex justify-center mb-2 gap-4">
                            <div className="w-132">
                                <InputField
                                    type="text"
                                    variant="bordered"
                                    size="md"
                                    label="Banner Hash"
                                    value={banner?.url ? banner.url : ''}
                                    onChange={(e) => {
                                        const temp = structuredClone(banner)
                                        temp.url = e.target.value.trim()
                                        temp.width = null
                                        temp.height = null
                                        temp.broken = false
                                        setBanner(temp)
                                    }}
                                    description={
                                        banner?.url
                                            ? ''
                                            : 'Image is recommended to be 1920x500'
                                    }
                                    isDisabled={isUploading || transactionId}
                                    isInvalid={banner?.broken}
                                />
                            </div>
                            <ImageUploadButton
                                id="banner-upload"
                                setIpfsHash={(hash) => {
                                    const temp = structuredClone(banner)
                                    temp.url = hash
                                    temp.width = null
                                    temp.height = null
                                    temp.broken = false
                                    setBanner(temp)
                                }}
                                disabled={transactionId}
                                setError={setImageUploadError}
                                setProgressPercentage={setUploadProgress}
                                setIsUploading={setIsUploading}
                            />
                        </div>
                        <DropAlert
                            condition={
                                banner?.wrongSize &&
                                !banner?.broken &&
                                banner?.url?.length > 0
                            }
                            severity="warning"
                            className="flex justify-center"
                        >
                            Incorrect dimensions! Banner is recommended to be
                            1920x500, but the selected image is {banner?.width}x
                            {banner?.height}
                        </DropAlert>
                        <DropAlert
                            condition={imageUploadError !== null}
                            severity="error"
                            className="flex justify-center"
                            title="An error occured during image upload"
                        >
                            {imageUploadError}
                        </DropAlert>
                        <DropAlert
                            condition={error !== null}
                            severity="error"
                            title="An error occured!"
                        >
                            {error}
                        </DropAlert>
                        <DropAlert
                            condition={transactionId !== null}
                            transactionId={transactionId}
                            severity="success"
                            title="Image successfully updated!"
                        >
                            Please note that it may take a couple of minutes to
                            see your changes reflected on the page
                        </DropAlert>
                        <div className="flex flex-wrap justify-center mt-5 gap-4">
                            <MainButton
                                className=""
                                fullWidth={false}
                                size="md"
                                onClick={() => saveCollection()}
                                isDisabled={
                                    transactionId ||
                                    isSaving ||
                                    !banner ||
                                    banner.broken ||
                                    banner.wrongSize ||
                                    !banner.url
                                }
                            >
                                Save
                            </MainButton>
                            <MainButton
                                className=""
                                fullWidth={false}
                                color="danger"
                                size="md"
                                onClick={() => close()}
                                isDisabled={isSaving}
                            >
                                Close
                            </MainButton>
                        </div>
                    </div>
                )}
            </DropPaper>
        </Dialog>
    )
}
